import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import { Box, CircularProgress } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import BaseHighchart from '../BaseHighchart';
import { ReduxState } from '../../../types';
import ChartFilter from '../ChartFilter';
import { navigateToChart } from '../../../utils/helpers';
import { ConversationSchema } from '../../../requests/api/apiTypes';
import ConversationModal from '../ConversationTab/conversationModal';
import { DEGREE_DAY_NAME, CHART_TITLE_GRADOS_DIA } from '../../../constants/graphs';
import apiClient from '../../../requests/api/apiClient';
import {
  ChartDataType,
  ModalState,
  initialModalState,
  DegreeDayChartProps,
} from './degreeDayTypes';
import { getDegreeDayChartConfig } from '../utils';
import { getConversationTooltip } from '../LineCharts/utils';
import ChartConfigDialog from '../ChartConfigDialog/ChartConfigDialog';
import DegreeDayConfigContent from './DegreeDayConfigContent';
import useChartConfigClick from '../../../hooks/useChartConfigClick';

function DegreeDayChart({
  chartView,
  sectorId,
  farmId,
  hideLegend = false,
  hideXValues = false,
  height = 300,
  title = CHART_TITLE_GRADOS_DIA,
  yAxisTitle = 'Grados Día',
  externalModalState,
  externalSetModalState,
  externalConversations,
  externalSetConversations,
  refreshKey,
  onConversationCreated,
  isWeatherStation,
}: DegreeDayChartProps) {
  const chartName = 'grados-dia';

  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    absoluteCategories: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const {
    interval,
  } = useSelector((state: ReduxState) => state.graphsFilter);
  const columnWidth = Math.min(300 / dayjs(interval?.endDate).diff(dayjs(interval?.startDate), 'day'), 18);

  const [internalModalState, setInternalModalState] = useState<ModalState>(initialModalState);
  const [internalConversations, setInternalConversations] = useState<ConversationSchema[]>([]);

  const modalState = externalModalState || internalModalState;
  const setModalState = externalSetModalState || setInternalModalState;
  const conversations = externalConversations || internalConversations;
  const setConversations = externalSetConversations || setInternalConversations;
  const selectedSector = useSelector((state: ReduxState) => state.currentSector.selectedSector);

  const createSeriesData = useCallback((preparedData: any) => [
    {
      type: 'column',
      name: 'Grados día',
      data: preparedData.degreeDay,
      yAxis: 0,
      color: '#FF0000',
      pointWidth: columnWidth,
    },
    {
      type: 'column',
      name: 'Horas frío día',
      data: preparedData.coldHours,
      yAxis: 0,
      color: '#8A2BE2',
      pointWidth: columnWidth,
    },
  ], [interval]);

  const handleError = useCallback((error: unknown) => {
    if (error instanceof Error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    } else {
      enqueueSnackbar('Ha ocurrido un error inesperado', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      if (interval) {
        const { data } = await apiClient.readings.getDegreeDayChartData({
          farmId,
          startDate: interval.startDate,
          endDate: interval.endDate,
          sectorId,
          telemetryType: selectedSector?.telemetryType,
          sn: selectedSector?.telemetryType === 'sn' ? selectedSector.weatherZoneId : undefined,
          chart: DEGREE_DAY_NAME,
          isWeatherStation,
        });

        const seriesData = createSeriesData(data);
        setChartData({
          series: seriesData,
          absoluteCategories: data.absoluteCategories,
        });
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    createSeriesData,
    interval,
    sectorId,
    farmId,
    selectedSector,
    refreshKey,
    handleError,
    isWeatherStation,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const customTooltip = useMemo(() => ({
    shared: true,
    useHTML: true,
    formatter(this: Highcharts.TooltipFormatterContextObject) {
      if (this.series.name === 'Comentarios') {
        return getConversationTooltip(this, conversations);
      }
      const header = '<div style="font-size: 14px; font-weight: bold; margin-bottom: 8px;">Resumen</div>';
      const subHeader = `<div style="font-size: 12px; margin-bottom: 8px;">${dayjs(this.x).format('MMM DD, YYYY')}</br></div>`;
      const points = this.points?.map(
        (point) => `<div style="margin: 4px 0"><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${Number(point.y).toFixed(2)}</b></div>`,
      ).join('');
      return `<div style="padding: 8px">${header}${subHeader}${points}</div>`;
    },
  }), [conversations]);

  useEffect(() => {
    if (!externalConversations) {
      const loadConversations = async () => {
        try {
          const { data } = await apiClient.conversation.conversationDetail(
            sectorId,
            DEGREE_DAY_NAME,
          );
          setInternalConversations(data.conversations);
        } catch (err) {
          handleError(err);
        }
      };

      loadConversations();
    }
  }, [sectorId, externalConversations, handleError]);

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = useCallback(() => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  }, [history, farmId, sectorId]);

  const chartConfig = useMemo(() => interval && getDegreeDayChartConfig(
    chartData,
    height,
    hideXValues,
    dayjs(interval?.startDate).valueOf(),
    dayjs(interval?.endDate).valueOf(),
  ), [interval, chartData.absoluteCategories]);

  return (
    <>
      <Box>
        {(isLoading) && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        )}
        <div style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
          <BaseHighchart
            chartView={chartView}
            sectorId={sectorId}
            filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
            title={title}
            interval={interval}
            yAxisTitle={yAxisTitle}
            series={chartData.series as Highcharts.SeriesOptionsType[]}
            tooltip={customTooltip}
            chartConfig={chartConfig || {}}
            setModalState={setModalState}
            conversations={conversations}
            absoluteCategories={chartData.absoluteCategories}
            legend={{ enabled: !hideLegend }}
            hideXValues={hideXValues}
            onConfigClick={handleConfigClick}
            onFullScreenClick={handleFullScreenClick}
            zoomSync={true}
            autoAdjustYAxis={true}
            graphName={DEGREE_DAY_NAME}
          />
        </div>
      </Box>
      <ConversationModal
        modalState={modalState}
        setModalState={setModalState}
        farmId={farmId}
        sectorId={sectorId}
        chart={DEGREE_DAY_NAME}
        setConversationsFunction={setConversations}
        onConversationCreated={onConversationCreated}
      />
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · Grados día"
      >
        <DegreeDayConfigContent
          sectorId={sectorId}
          onConfigSaved={() => fetchData()}
        />
      </ChartConfigDialog>
    </>
  );
}

export default DegreeDayChart;
